import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useParams } from 'react-router'
import { FlowLayout } from '@/ds'
import { useStay } from '@/app/stay/StayProvider'
import { useAppNavigate, withQuery } from '@/lib/query-params'
import { checkinService, EMPTY_GUEST, stepErrors } from '@/services/checkin'
import type { GuestData, GuestField } from '@/services/checkin'
import { useCheckinFlow } from '../CheckinFlow'
import type { CheckinDraft } from '../CheckinFlow'
import { stepPath } from '../flow'
import { errorsAfterGuestChange, GuestFields } from '../fields'
import type { FieldErrors } from '../fields'
import { useApiLang, useSave } from '../useSave'

/** Nombres de `fieldsRequiered` del PMS → campos del parte (app actual: Fill.tsx y midniQrData). */
const PMS_FIELD: Record<string, GuestField> = {
  name: 'name',
  firstName: 'name',
  surname1: 'surname1',
  surname2: 'surname2',
  identifier: 'identifier',
  identifierTypeId: 'identifierTypeId',
  supportNumber: 'supportNumber',
  bornDate: 'bornDate',
  sexId: 'sexId',
  sex: 'sexId',
  email: 'email',
  countryId: 'countryId',
  country: 'countryId',
  provinceId: 'provinceId',
  province: 'provinceId',
  municipalityId: 'municipalityId',
  city: 'city',
  address: 'address',
  postalCode: 'postalCode',
  relationship: 'relationship',
}
const ORDER: GuestField[] = ['name', 'surname1', 'surname2', 'email', 'identifierTypeId', 'identifier', 'supportNumber', 'bornDate', 'address', 'postalCode', 'city', 'countryId', 'provinceId', 'municipalityId', 'sexId', 'relationship']

/**
 * Completar datos de una persona ya registrada (app actual: /checkin/:id/fill). Solo los campos que el PMS
 * marca en `fieldsRequiered`; después, la firma si también falta. Guarda con E5 y el id de la persona.
 */
export function Completar() {
  const { t } = useTranslation()
  const { id } = useParams()
  const { go } = useAppNavigate()
  const lang = useApiLang()
  const { reload } = useStay()
  const { stay, draft, start, updateGuest, update } = useCheckinFlow()
  const traveler = stay.registration.travelers.find((tr) => tr.id === id)
  const [errors, setErrors] = useState<FieldErrors>({})
  const { saving, error, run } = useSave()

  useEffect(() => {
    if (!traveler || (draft.mode === 'complete' && draft.travelerId === traveler.id)) return
    const d = traveler.details
    const guest: GuestData = {
      ...EMPTY_GUEST,
      name: d.name,
      surname1: d.surname1,
      surname2: d.surname2,
      email: d.email ?? '',
      identifier: d.identifier ?? '',
      identifierTypeId: d.identifierTypeId ?? '',
      supportNumber: d.supportNumber ?? '',
      bornDate: d.bornDate ?? '',
      sexId: d.sexId ?? '',
      countryId: d.countryId ?? '',
      provinceId: d.provinceId ?? '',
      municipalityId: d.municipalityId ?? '',
      city: d.city ?? '',
      address: d.address ?? '',
      postalCode: d.postalCode ?? '',
      relationship: d.relationship ? { responsibleAdultId: d.relationship.idOfAdult, childRelationshipId: d.relationship.relationshipOfJunior, adultRelationshipId: d.relationship.relationshipOfAdult } : null,
    }
    const next: CheckinDraft = {
      mode: 'complete',
      persona: traveler.canDelete ? 'companion' : 'holder',
      guest,
      phone: '',
      method: 'manual',
      entry: 'manual',
      docCountryId: guest.countryId,
      documentTypeId: '',
      doubtful: [],
      travelerId: traveler.id,
      documents: [],
      missingFields: traveler.missingFields,
      signaturePending: traveler.signaturePending,
    }
    start(next)
    // Solo sin datos faltantes (solo firma) se va directo a firmar.
    if (!traveler.missingFields.some((f) => f !== 'signature')) go(stepPath('7'), { replace: true })
  }, [traveler, draft.mode, draft.travelerId, start, go])

  if (!traveler) return <Navigate to={withQuery(stepPath('1'), window.location.search)} replace />

  const fields = ORDER.filter((f) => traveler.missingFields.some((m) => PMS_FIELD[m] === f))
  const onGuestChange = (patch: Partial<typeof draft.guest>) => {
    updateGuest(patch)
    setErrors((current) => errorsAfterGuestChange(current, patch, stay, draft.guest))
  }
  const submit = async () => {
    const e = stepErrors(fields, stay, draft.guest)
    setErrors(e)
    if (Object.keys(e).length > 0) return
    const ok = await run(() => checkinService.completeGuest(stay, traveler.id, draft.guest, lang).then(() => true))
    if (!ok) return
    if (traveler.signaturePending) {
      update({ mode: 'complete', travelerId: traveler.id })
      return go(stepPath('7'))
    }
    go(stepPath('1'))
    reload()
  }

  return (
    <FlowLayout
      progress={{ step: 1, total: traveler.signaturePending ? 2 : 1, backLabel: t('v2.checkin.atras.ocupantes'), onBack: () => go(stepPath('1')) }}
      title={t('v2.legacy.helpers.Missing_data')}
      intro={`${traveler.name} ${traveler.surname}`.trim()}
      primary={{ label: t('v2.checkin.continuar'), onClick: () => void submit(), loading: saving }}
      footerMessage={error}
    >
      <GuestFields
        stay={stay}
        guest={draft.guest}
        fields={fields}
        errors={errors}
        onChange={onGuestChange}
        onBlurField={(f) => setErrors((p) => ({ ...p, [f]: stepErrors([f], stay, draft.guest)[f] }))}
        focusRequest={draft.mode === 'complete' && draft.travelerId === traveler.id ? `complete-${traveler.id}` : undefined}
      />
    </FlowLayout>
  )
}
